/**
 * Validates ONE step being edited in the drawer, before it is committed to the graph.
 *
 * Separate from validateMasterSuiteSteps (which grades a whole saved suite) because the
 * drawer needs a field → message map to render inline and to gate its Save button. The
 * rules are the same ones the suite-level save applies; keeping them here means a draft
 * can never commit something the suite-level save would then reject.
 *
 * @param draft      the working copy — NOT yet in the graph
 * @param committed  the rows currently in the graph
 * @param mode       "new" | "edit" — an edit excludes itself from the duplicate check
 * @returns {{}} field → message, empty when the draft may be committed
 */
export function validateDraftStep(draft, committed = [], mode = "new") {
  const errors = {};
  if (!draft) return errors;

  const name = (draft.stepReference || "").trim();
  const isMasterSuite = draft.type === "Master Suite";

  if (!draft.type) errors.type = "Choose a type";
  if (!name) errors.stepReference = "Step name is required";

  // A "Master Suite" step references another suite, so it carries no category of its own.
  if (!isMasterSuite) {
    if (!draft.category) errors.category = "Category is required";
    if (!draft.subcategory) errors.subcategory = "Sub category is required";
    // NOTE: the suite-level save compares type === "task" in lower case while the stored
    // value is "Task", so this rule has never actually fired there. Comparing correctly
    // here means a draft can be rejected for something an older suite got away with.
    if (draft.type === "Task" && !draft.taskName) {
      errors.taskName =
        draft.category === "ui" ? "Select a UI suite" : "Select a task";
    }
  }

  if (name) {
    const clash = committed.some(
      (r) =>
        (mode === "new" || r.key !== draft.key) &&
        (r.stepReference || "").trim() === name,
    );
    if (clash) errors.stepReference = "A step with this name already exists";
  }

  return errors;
}

// Shared step validation for the master-suite builder tables (New + Copy).
// Returns the per-row field-error map (used to red-border inputs) plus a list of
// human-readable issues for the toast/notification. Single source of truth so the
// two tables don't each re-implement the same checks.
export function validateMasterSuiteSteps(dataSource = []) {
  const errorFields = {};
  const issues = [];
  const seen = new Set();

  dataSource.forEach((item) => {
    const label = item.stepReference
      ? `Step ${item.sequence} "${item.stepReference}"`
      : `Step ${item.sequence}`;

    if (!item.type) {
      errorFields[item.key] = { ...errorFields[item.key], type: true };
      issues.push(`${label}: choose a Type`);
    }

    if (!item.stepReference) {
      errorFields[item.key] = {
        ...errorFields[item.key],
        stepReference: "Step name cannot be blank",
      };
      issues.push(`Step ${item.sequence}: enter a Step name`);
    } else if (seen.has(item.stepReference)) {
      errorFields[item.key] = {
        ...errorFields[item.key],
        stepReference: "Step name already exists",
      };
      issues.push(`Duplicate step name "${item.stepReference}"`);
    } else {
      seen.add(item.stepReference);
    }
  });

  validateStepTree(dataSource, errorFields, issues);

  return {
    errorFields,
    issues,
    hasErrors: Object.keys(errorFields).length > 0,
  };
}

/**
 * Checks the fan-out links: every parent must exist, and the links must not form a cycle.
 *
 * The drawer's picker already makes both unreachable — it hides the step itself and
 * everything beneath it. This is the backstop for links that did NOT come through the
 * picker: a suite saved by an older build, a copied suite, or a rename that raced. It
 * matters because the runner schedules a step only after its parent finishes, so a cycle
 * would not error — every step in it would simply never run, and the suite would report
 * a puzzling success with nothing done.
 */
function validateStepTree(rows, errorFields, issues) {
  const byRef = new Map();
  // Names that appear more than once. While a name is ambiguous this map cannot say which
  // row a link points at, so every tree conclusion about those rows is guesswork — and the
  // duplicate itself is already reported, which is the error the user can act on. Skipping
  // them is what stops a half-typed name ("test" on the way to "test 2") producing a
  // second, contradictory message about running after itself.
  const duplicated = new Set();
  rows.forEach((r) => {
    if (!r.stepReference) return;
    if (byRef.has(r.stepReference)) duplicated.add(r.stepReference);
    byRef.set(r.stepReference, r);
  });

  const flag = (item, message) => {
    errorFields[item.key] = { ...errorFields[item.key], parentId: true };
    issues.push(message);
  };

  rows.forEach((item) => {
    if (!item.parentId) return;
    if (duplicated.has(item.stepReference) || duplicated.has(item.parentId)) return;
    const label = `Step ${item.sequence} "${item.stepReference || ""}"`;

    if (!byRef.has(item.parentId)) {
      flag(item, `${label}: runs after "${item.parentId}", which no longer exists`);
      return;
    }

    // A step that lists ITSELF as its parent — named precisely rather than described as a
    // "loop", which reads as nonsense for a single step. Reachable through a duplicate
    // name: this graph is keyed by NAME, so two steps called "test" makes the second one
    // its own parent.
    if (item.parentId === item.stepReference) {
      flag(item, `${label}: runs after itself`);
      return;
    }

    // Climb the parent chain. A cycle is only THIS step's problem if the climb comes back
    // to THIS step after at least one hop. Hitting a repeat further up means the loop is
    // upstream, and the step that actually closes it reports it itself.
    //
    // The previous version seeded the path with this step and flagged ANY repeat, so a
    // single broken link poisoned every step beneath it — which is how a plain 1 → 2 chain
    // came to report "these steps wait on each other" against step 2, a step with nothing
    // wrong with it. The hop bound stays as a backstop.
    const seen = new Set();
    let cursor = byRef.get(item.parentId);
    for (let hops = 0; cursor && hops <= rows.length; hops += 1) {
      if (cursor.stepReference === item.stepReference) {
        flag(item, `${label}: these steps wait on each other in a loop`);
        return;
      }
      if (seen.has(cursor.stepReference)) return; // upstream loop — not this step's to report
      seen.add(cursor.stepReference);
      cursor = cursor.parentId ? byRef.get(cursor.parentId) : null;
    }
  });
}
